/**
 * Strapi client for fetching content
 */

import { StrapiResponse, StrapiImageSource } from './types';
import { siteConfig } from '@/config/site'; // Import siteConfig

// Function to convert relative Strapi image URLs to absolute URLs
export function urlForImage(image: StrapiImageSource | null) {
  if (!image) return null;
  
  // console.log('Image data structure:', JSON.stringify(image));
  
  // Check for various image data structures that might be coming from the API
  if (image.data && image.data.attributes && image.data.attributes.url) {
    const imageUrl = image.data.attributes.url;
    if (imageUrl.startsWith('http')) { return { url: () => imageUrl }; }
    return { url: () => `${siteConfig.strapiUrl}${imageUrl}` }; // Use siteConfig
  }
  
  // Check for direct attributes structure
  if (image.attributes && image.attributes.url) {
    const imageUrl = image.attributes.url;
    if (imageUrl.startsWith('http')) { return { url: () => imageUrl }; }
    return { url: () => `${siteConfig.strapiUrl}${imageUrl}` }; // Use siteConfig
  }
  
  // Check for direct URL property in the image object
  if (image.url) {
    const imageUrl = image.url;
    if (imageUrl.startsWith('http')) { return { url: () => imageUrl }; }
    return { url: () => `${siteConfig.strapiUrl}${imageUrl}` }; // Use siteConfig
  }
  
  // If no valid URL structure is found
  console.error('No valid URL structure found in image:', image);
  return null;
}

// Fetch API helper with proper typing
async function fetchAPI<T>(endpoint: string): Promise<StrapiResponse<T> | null> {
  try {
    // Ensure we're using the correct API path and siteConfig for base URL
    const url = `${siteConfig.strapiUrl}/api${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
    
    // console.log(`Fetching from: ${url}`);
    
    const res = await fetch(url, {
      next: { revalidate: 60 }, // Revalidate every 60 seconds
    });
    
    if (!res.ok) {
      console.error(`API error: ${res.status} ${res.statusText}`);
      return null;
    }
    
    const data = await res.json();
    console.log(`Fetched data structure:`, Object.keys(data));
    return data as StrapiResponse<T>;
  } catch (error) {
    console.error('Error fetching from Strapi:', error);
    return null;
  }
}

// Function to fetch blog posts - runs during build
export async function getPosts() {
  // Use the correct API endpoint for Strapi v4
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data = await fetchAPI<any[]>('/blogs?populate=coverImage&sort=publishedDate:desc');
  
  if (!data || !data.data || !Array.isArray(data.data)) {
    console.error('Invalid response structure from Strapi API:', data);
    return [];
  }
  
  if (data.data.length > 0) {
    console.log('Sample post data:', JSON.stringify(data.data[0], null, 2));
  }
  
  // Transform Strapi response structure to match our app needs
  return data.data.map((post) => {
    console.log(`Processing post ${post.id} - ${post.attributes?.title || 'Untitled'}`);
    
    // Extract cover image directly from the data structure
    let coverImage = null;
    
    try {
      // Check if we have a coverImage field
      if (post.attributes?.coverImage?.data) {
        console.log(`Post ${post.id} has coverImage data`);
        coverImage = {
          data: post.attributes.coverImage.data,
          attributes: post.attributes.coverImage.data.attributes
        };
      } 
      // Handle legacy structure or direct URL
      else if (post.coverImage) {
        console.log(`Post ${post.id} has direct coverImage`);
        coverImage = post.coverImage;
      }
    } catch (error) {
      console.error(`Error extracting coverImage for post ${post.id}:`, error);
    }
    
    // Log the extracted coverImage
    if (coverImage) {
      console.log(`CoverImage for post ${post.id}:`, JSON.stringify(coverImage, null, 2));
    } else {
      console.log(`No coverImage for post ${post.id}`);
    }
    
    // Get attributes from either structure
    const attributes = post.attributes || post;
    
    return {
      _id: post.id,
      title: attributes.title || '',
      slug: { current: attributes.slug || '' },
      publishedDate: attributes.publishedDate || '',
      coverImage,
      excerpt: attributes.excerpt || '',
    };
  });
}

// Function to fetch a single blog post - runs during build
export async function getPost(slug: string) {
  try {
    // First, get all posts to find the ID for the requested slug
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const allPosts = await fetchAPI<any[]>('/blogs');
    
    if (!allPosts || !allPosts.data || !Array.isArray(allPosts.data) || allPosts.data.length === 0) {
      console.error('No posts found in the API');
      return null;
    }
    
    // Find the post with the matching slug
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const postWithSlug = allPosts.data.find((post: any) => {
      const postSlug = post.slug || '';
      console.log(`Comparing slug: "${postSlug}" with requested: "${slug}"`);
      return postSlug === slug;
    });
    
    if (!postWithSlug) {
      console.error(`No post found with slug: ${slug}`);
      return null;
    }
    
    const postId = postWithSlug.id;
    console.log(`Found post with ID: ${postId} for slug: ${slug}`);
    
    // Now get the full post with populated fields
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = await fetchAPI<any>(`/blogs/${postId}?populate=coverImage,body`);
    
    if (!data || !data.data) {
      console.error(`Failed to fetch post with ID: ${postId}`);
      return null;
    }
    
    const postData = data.data;
    console.log('Single post data from Strapi:', JSON.stringify(postData, null, 2));
    
    // Handle Strapi V4 structure where content might be inside attributes
    const attributes = postData.attributes || {};
    
    // Extract cover image properly
    let coverImage = null;
    if (attributes?.coverImage?.data) {
      coverImage = {
        data: attributes.coverImage.data,
        attributes: attributes.coverImage.data?.attributes
      };
    } else if (postData.coverImage) {
      // Handle direct coverImage structure
      coverImage = postData.coverImage;
    }
    
    // Transform to match our app structure with direct access
    return {
      _id: postData.id,
      title: attributes?.title || postData.title || '',
      publishedDate: attributes?.publishedDate || postData.publishedDate || '',
      coverImage,
      body: attributes?.body || postData.body || [],
      excerpt: attributes?.excerpt || postData.excerpt || '',
      categories: attributes?.categories || postData.categories || [],
      tags: attributes?.tags || postData.tags || [],
      seo: attributes?.seo || postData.seo || null
    };
  } catch (error) {
    console.error(`Error fetching post with slug ${slug}:`, error);
    return null;
  }
}

// Function to get all blog post slugs - used for generateStaticParams
export async function getAllPostSlugs() {
  try {
    // Get all posts to extract slugs
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = await fetchAPI<any[]>('/blogs?sort=publishedDate:desc');
    
    if (!data || !data.data || !Array.isArray(data.data)) {
      console.error('No posts found for slug generation');
      return [];
    }
    
    // Extract slugs directly from the posts
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const slugs = data.data.map((post: any) => {
      const slug = post.slug || '';
      console.log(`Found slug for static params: "${slug}"`);
      return slug;
    });
    
    console.log('All slugs for static generation:', slugs);
    return slugs;
  } catch (error) {
    console.error('Error fetching slugs:', error);
    return [];
  }
}

// Function to fetch categories
export async function getCategories() {
  const data = await fetchAPI<{id: number, attributes: {name: string, description?: string}}[]>('/categories');
  
  if (!data || !data.data || !Array.isArray(data.data)) return [];
  
  return data.data.map((category: {id: number, attributes: {name: string, description?: string}}) => ({
    id: category.id,
    name: category.attributes.name,
    description: category.attributes.description,
  }));
}

// Function to fetch tags
export async function getTags() {
  const data = await fetchAPI<{id: number, attributes: {name: string}}[]>('/tags');
  
  if (!data || !data.data || !Array.isArray(data.data)) return [];
  
  return data.data.map((tag: {id: number, attributes: {name: string}}) => ({
    id: tag.id,
    name: tag.attributes.name,
  }));
} 